import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import clsx from 'clsx'
import { useEffect, useRef, useState } from 'react'
import { Edit, FileDownload, OverflowMenu, TrashDelete } from '@/assets/icons'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { downloadAgentV2ConversationDocx } from '@/agent-v2/conversations/fetch/download-agent-v2-conversation-docx'
import { GlobalToastType, kToastDurationMediumMs, kToastDurationShortMs, kTooltipHoverDelayMs } from '@/constants/constants-ui'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { IconButton } from '@/components/icon-button/IconButton'
import type { IconButtonColor } from '@/components/icon-button/types'
import type { RootState } from '@/store/store'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { selectAgentV2ConversationFromId } from '@/agent-v2/conversations/store/selectors'
import { useIsAgentV2ConversationCreator } from '@/agent-v2/conversations/hooks/useIsAgentV2ConversationCreator'
import { MenuContent, MenuItemGroup, MenuSeparator } from '@/components/menu/MenuContent'
import { MenuItem } from '@/components/menu/MenuItem'

type AgentV2ConversationMenuProps = {
  conversationId: string
  iconSize?: 'sm' | 'md' | 'lg'
  iconColor?: IconButtonColor
  isRowActive?: boolean
  onSelectDelete: () => void
  onSelectRename: () => void
  onOpenChange?: (open: boolean) => void
  open?: boolean
  showTriggerOnHover?: boolean
  /** Whether this chat has unread messages this session. */
  unread?: boolean
  /** Trigger's `tabIndex`. Pass -1 to remove it from the normal Tab sequence when the row itself
   * provides its own way to reach it (e.g. an ArrowRight shortcut) — defaults to normal Tab order. */
  triggerTabIndex?: number
  /* Accessible name for the trigger. Defaults to "Chat actions". */
  triggerAriaLabel?: string
  /* Hover label for the trigger. */
  tooltipText?: string
}

export default function AgentV2ConversationMenu({
  conversationId,
  iconSize = 'sm',
  iconColor = 'neutral',
  isRowActive = false,
  onSelectDelete,
  onSelectRename,
  onOpenChange,
  open,
  showTriggerOnHover = false,
  unread = false,
  triggerTabIndex,
  triggerAriaLabel = 'Chat actions',
  tooltipText,
}: AgentV2ConversationMenuProps) {
  const dispatch = useAppDispatch()
  const { trackEvent } = useAnalytics()
  const conversation = useAppSelector((state: RootState) => selectAgentV2ConversationFromId(state, { conversationId }))
  const isConversationCreator = useIsAgentV2ConversationCreator(conversationId)
  const [isDownloading, setIsDownloading] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)

  // Downloading disables the trigger, which drops focus, so put it back when the download ends unless
  // the user has moved on themselves.
  const wasDownloadingRef = useRef(isDownloading)
  useEffect(() => {
    const wasDownloading = wasDownloadingRef.current
    wasDownloadingRef.current = isDownloading
    if (!wasDownloading || isDownloading || document.activeElement !== document.body) return
    triggerRef.current?.focus()
  }, [isDownloading])

  if (!conversation) return null

  const showUnreadIndicator = unread && showTriggerOnHover && !isRowActive && !isDownloading

  async function handleDownload() {
    trackEvent(AnalyticsEvent.DownloadConversationToDocx)
    setIsDownloading(true)
    try {
      const filename = await downloadAgentV2ConversationDocx(conversationId)
      dispatch(
        openGlobalToast({
          type: GlobalToastType.SUCCESS,
          message: `${filename} downloaded`,
          durationMs: kToastDurationShortMs,
        })
      )
    } catch {
      dispatch(
        openGlobalToast({
          type: GlobalToastType.ERROR,
          message: 'Chat download failed. Please try again.',
          durationMs: kToastDurationMediumMs,
        })
      )
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <DropdownMenu.Root open={open} onOpenChange={onOpenChange}>
      <DropdownMenu.Trigger asChild disabled={isDownloading}>
        <IconButton
          ref={triggerRef}
          aria-label={isDownloading ? 'Downloading chat' : triggerAriaLabel}
          icon={
            showUnreadIndicator ? (
              <span className="flex items-center justify-center">
                <OverflowMenu className="h-full w-full lg:hidden lg:group-hover:block lg:group-focus-within:block group-data-[state=open]/chat-actions:block" />
                <span className="hidden h-1.5 w-1.5 rounded-full bg-brand-500 lg:block group-hover:hidden group-focus-within:hidden group-data-[state=open]/chat-actions:hidden" />
              </span>
            ) : (
              <OverflowMenu />
            )
          }
          variant="plain"
          color={iconColor}
          size={iconSize}
          loading={isDownloading}
          type="button"
          tabIndex={triggerTabIndex}
          tooltipText={tooltipText}
          tooltipPlacement="bottom"
          tooltipHoverDelayMs={kTooltipHoverDelayMs}
          disableTooltipInteractive
          disableTooltipFocusListener
          className={clsx(
            'group/chat-actions transition-opacity duration-200 ease-in-out',
            showTriggerOnHover
              ? clsx(
                  'lg:opacity-0 lg:group-hover:opacity-100 lg:group-focus-within:opacity-100 data-[state=open]:opacity-100',
                  (isRowActive || isDownloading || showUnreadIndicator) && 'lg:opacity-100'
                )
              : 'opacity-100'
          )}
          onClick={(event) => {
            event.stopPropagation()
            event.preventDefault()
          }}
        />
      </DropdownMenu.Trigger>
      <MenuContent align="end" sideOffset={3}>
        <MenuItemGroup>
          {isConversationCreator && (
            <MenuItem
              label="Rename chat"
              icon={Edit}
              onSelect={() => {
                trackEvent(AnalyticsEvent.OpenConversationRetitleDialog)
                onSelectRename()
              }}
              variant="neutral"
            />
          )}
          <MenuItem label="Download chat" icon={FileDownload} onSelect={() => void handleDownload()} variant="neutral" />
        </MenuItemGroup>
        {isConversationCreator && (
          <>
            <MenuSeparator />
            <MenuItemGroup>
              <MenuItem
                label="Delete chat"
                icon={TrashDelete}
                onSelect={() => {
                  trackEvent(AnalyticsEvent.OpenConversationDeletionDialog)
                  onSelectDelete()
                }}
                variant="destructive"
              />
            </MenuItemGroup>
          </>
        )}
      </MenuContent>
    </DropdownMenu.Root>
  )
}
